import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, PASSWORD_PROTECTED_MESSAGE } from './pdfjs'
import type { PageProps } from 'react-pdf'
import { colors, fontSize, shadow, spacing } from '../../styles/theme'

type PageCallback = Parameters<NonNullable<PageProps['onLoadSuccess']>>[0]

export interface PdfPageInfo {
  pageIndex: number
  /** Rendered px per PDF point. */
  scale: number
  widthPt: number
  heightPt: number
}

interface PdfPagesProps {
  file: Blob | null
  maxWidth?: number
  /** Absolutely-positioned layer drawn over each page, sized to the rendered page. */
  renderOverlay?: (info: PdfPageInfo) => React.ReactNode
  onError?: (message: string) => void
  /**
   * Asks for a protected PDF's password (with an app modal, never prompt()): <c>incorrect</c> is true when the last one
   * was wrong. Resolve null to give up, which fails the load. Without it a protected PDF simply fails to load.
   */
  askPassword?: (incorrect: boolean) => Promise<string | null>
}

/** Every page of a PDF stacked vertically, scaled to fit the container width. */
/** pdf.js PasswordResponses.INCORRECT_PASSWORD. */
const INCORRECT_PASSWORD = 2

export default function PdfPages({ file, maxWidth = 816, renderOverlay, onError, askPassword }: PdfPagesProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [numPages, setNumPages] = useState(0)
  const [sizes, setSizes] = useState<Record<number, { w: number; h: number }>>({})
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setContainerWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const width = Math.max(200, Math.min(containerWidth || maxWidth, maxWidth))

  // Stable, so the Document does not reload on every render; the latest askPassword is read through a ref.
  const askRef = useRef(askPassword)
  askRef.current = askPassword
  const hasAsk = !!askPassword
  const onPassword = useMemo(() => hasAsk
    ? (callback: (password: string) => void, reason: number) => {
        void (askRef.current?.(reason === INCORRECT_PASSWORD) ?? Promise.resolve(null)).then(password =>
          (callback as (p: string | Error) => void)(password ?? new Error(PASSWORD_PROTECTED_MESSAGE)))
      }
    : undefined, [hasAsk])

  const onPageLoad = useCallback((index: number, page: PageCallback) => {
    const vp = page.getViewport({ scale: 1 })
    setSizes(s => (s[index]?.w === vp.width && s[index]?.h === vp.height ? s : { ...s, [index]: { w: vp.width, h: vp.height } }))
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.md }}>
      {file && (
        <Document
          file={file}
          {...(onPassword && { onPassword })}
          onLoadSuccess={({ numPages }) => { setNumPages(numPages); setSizes({}) }}
          onLoadError={e => {
            // pdf.js reports a refused or abandoned password as its own PasswordException ("No password given").
            const m = e.name === 'PasswordException' ? PASSWORD_PROTECTED_MESSAGE : e.message || 'Failed to load the document.'
            setLoadError(m); onError?.(m)
          }}
          loading={<div style={{ color: colors.textSecondary, fontSize: fontSize.sm, padding: spacing.lg }}>Loading document…</div>}
          error={<div style={{ color: colors.error, fontSize: fontSize.sm, padding: spacing.lg }}>{loadError || 'Failed to load the document.'}</div>}
        >
          {Array.from({ length: numPages }, (_, i) => {
            const size = sizes[i]
            return (
              <div key={i} style={{ position: 'relative', width, boxShadow: shadow.md, background: '#fff', marginBottom: spacing.md }}>
                <Page
                  pageNumber={i + 1}
                  width={width}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onLoadSuccess={p => onPageLoad(i, p)}
                />
                {size && renderOverlay && (
                  <div style={{ position: 'absolute', inset: 0 }}>
                    {renderOverlay({ pageIndex: i, scale: width / size.w, widthPt: size.w, heightPt: size.h })}
                  </div>
                )}
              </div>
            )
          })}
        </Document>
      )}
    </div>
  )
}
